<template>
  <PrimeDialog
    ref="maxDialog"
    :class="dialogClass"
    :closable="false"
    :closeOnEscape="true"
    :data-test="'modal'"
    :dismissableMask="true"
    :draggable="false"
    :maximizable="is_maximizable"
    :modal="true"
    :style="dialogStyle"
    :visible="visible_prop"
    @hide="onCancel"
    @update:visible="$emit('update:visible', $event)"
    @show="maximizeDialog"
    :baseZIndex="z_index"
  >
    <template #header>
      <div v-if="is_show_header">
        <div class="title is-4">
          <slot name="title">
            {{ $t('modal.labels.areYouSure') }}
          </slot>
        </div>
        <div
          v-if="$slots.subtitle"
          class="subtitle is-5"
        >
          <slot name="subtitle">
            {{ $t('modal.labels.areYouSure') }}
          </slot>
        </div>
        <div
          v-if="$slots.message"
          class="pt-5"
        >
          <slot name="message" />
          <div
            v-if="$slots.sub_message"
            class="font-bold"
          >
            <slot name="sub_message" />
          </div>
        </div>
        <div
          v-if="$slots.notification"
          class="notification font-medium"
        >
          <slot name="notification" />
        </div>
        <div
          v-if="$slots.notification_danger"
          class="p-message p-message-error font-medium"
          data-test="notification-danger"
        >
          <slot name="notification_danger" />
        </div>
      </div>
    </template>
    <div v-if="$slots.content">
      <slot name="content" />
    </div>

    <template #footer>
      <div
        v-if="is_show_footer"
        class="buttons"
      >
        <ButtonPrimary
          v-if="$slots.ButtonPrimary && is_show_buttons"
          :is_disabled="!is_confirmed"
          :is_loading="is_loading"
          :action="action"
          :data-test="props.data_test_primary"
          @onClick="onSubmit"
        >
          <slot name="ButtonPrimary" />
        </ButtonPrimary>

        <ButtonPrimary
          v-if="$slots.ButtonLoading && is_show_buttons"
          is_disabled
          is_loading
        >
          <slot name="ButtonLoading">
            {{ $t('button.actions.loading') }}
          </slot>
        </ButtonPrimary>

        <ButtonDestructive
          v-if="$slots.ButtonDestructive && is_show_buttons"
          filled
          data-test="modal-confirm-primary"
          :is_disabled="!is_confirmed"
          @onClick="onSubmit"
        >
          <slot name="ButtonDestructive">
            {{ $t('button.actions.delete') }}
          </slot>
        </ButtonDestructive>

        <ButtonDestructive
          v-else-if="$slots.ButtonDestructiveLoading && is_show_buttons"
          filled
          data-test="modal-confirm-primary"
          is_disabled
          is_loading
          @click="onSubmit"
        >
          <slot name="ButtonDestructive">
            {{ $t('button.actions.loading') }}
          </slot>
        </ButtonDestructive>

        <ButtonSecondary
          v-if="$slots.ButtonSecondary && is_show_buttons"
          data-test="modal-confirm-secondary"
          @onClick="$emit('actionSecondary')"
        >
          <slot name="ButtonSecondary" />
        </ButtonSecondary>

        <ButtonTertiary
          v-if="is_cancel && is_show_buttons"
          data-test="modal-confirm-tertiary"
          @onClick="onCancel"
        />

        <div
          v-else
          class="flex grow justify-end"
        >
          <slot name="content-footer" />
        </div>
      </div>
    </template>
  </PrimeDialog>
</template>

<script setup>
import { computed, onMounted, onUnmounted, ref } from 'vue';
import ButtonDestructive from '@/app/shared_components/buttons/ButtonDestructive.vue';
import { useMqService } from '@/plugins/mq';

const props = defineProps({
  visible_prop: { type: Boolean, default: true },
  action: { type: String, default: null },
  is_cancel: { type: Boolean, default: true },
  can_overflow: { type: Boolean },
  is_confirmed: { type: Boolean, default: true },
  is_loading: { type: Boolean },
  is_fixed_height: { type: Boolean },
  is_flexible_width: { type: Boolean },
  is_medium: { type: Boolean },
  is_show_buttons: { type: Boolean, default: true },
  is_show_footer: { type: Boolean, default: true },
  is_show_header: { type: Boolean, default: true },
  is_small: { type: Boolean, default: true },
  start_maximized: { type: Boolean },
  prevent_automatic_close: { type: Boolean },
  prevent_enter_shortcut: { type: Boolean },
  prevent_auto_maximize_mobile: { type: Boolean },
  is_maximizable: { type: Boolean, default: true },
  hide_maximize_button: { type: Boolean, default: true },
  data_test_primary: { type: String, default: 'modal-confirm-primary' },
  z_index: { type: Number, default: 0 },
});

const emit = defineEmits(['actionCancel', 'actionPrimary', 'actionSecondary', 'update:visible']);

const mq = useMqService();

const dialogStyle = computed(() => {
  const style = {};

  if (props.is_fixed_height) {
    style.height = 'calc(100vh - 200px)';
  }

  if (props.is_small) {
    style.width = '360px';
  } else if (props.is_medium) {
    style.width = '600px';
  } else if (props.is_flexible_width) {
    style.width = '100%';
  }

  return style;
});

const dialogClass = computed(() => {
  const classes = [''];

  if (props.is_small || props.is_medium || props.is_flexible_width) {
    classes.push('flex-center');
  }

  if (props.hide_maximize_button) {
    classes.push('hide-maximize-btn');
  }

  return classes;
});

const maxDialog = ref();
function maximizeDialog() {
  if ((!mq.isMobile && !props.start_maximized) || (mq.isMobile && props.prevent_auto_maximize_mobile)) return;

  maxDialog.value.maximized = true;
}

const onCancel = () => {
  emit('update:visible', false);
  emit('actionCancel');
};

const onSubmit = () => {
  emit('actionPrimary');
};

const handleEnterKey = (e) => {
  if (props.prevent_enter_shortcut || !props.is_confirmed) return;
  if (e.key === 'Enter') {
    e.preventDefault();
    onSubmit();
  }
};

onMounted(() => {
  document.addEventListener('keydown', handleEnterKey);
});

onUnmounted(() => {
  document.removeEventListener('keydown', handleEnterKey);
});
</script>

<style>
.p-dialog-footer {
  display: flex;
  justify-content: flex-start !important;
  align-items: center;
  width: 100%;
  background-color: #f3f4f6;
  padding: 1rem !important;
  border-bottom-left-radius: inherit;
  border-bottom-right-radius: inherit;
}
.p-dialog-header {
  display: flex;
  justify-content: space-between;
  width: 100%;
}
.p-dialog-maximized .p-dialog-content {
  align-self: flex-start;
  width: 100%;
}
.hide-maximize-btn .p-dialog-maximize-button {
  display: none !important;
}
</style>
